import React, { memo, useEffect, useRef } from "react";
import { Box, Button, Typography } from "@mui/material";
import { RoundedIcon } from "@shared/components";
import { ReactComponent as ExclamationIcon } from "@assets/svg/exclamation.svg";
import { bankExtractionCopy } from "@investorRequest/constants/bankExtractionCopy";
import { paymentMethodLabel } from "@investorRequest/utils/applyExtractionResult";

interface Props {
  detectedPaymentMethod?: string | null;
  onTryAnother: () => void;
  onFillManually: () => void;
}

const BankExtractionTotalFailureCard = ({ detectedPaymentMethod, onTryAnother, onFillManually }: Props) => {
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const headline =
    detectedPaymentMethod && detectedPaymentMethod !== "unknown"
      ? bankExtractionCopy.totalFailureHeadlineWithMethod(paymentMethodLabel(detectedPaymentMethod))
      : bankExtractionCopy.totalFailureHeadline;

  useEffect(() => {
    headlineRef.current?.focus();
  }, []);

  return (
    <Box
      role="alert"
      data-testid="bank-extraction-total-failure-card"
      sx={{
        bgcolor: "#FCEFEC",
        border: "1px solid",
        borderColor: "error.main",
        borderRadius: 1,
        p: 4,
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          bgcolor: "#FCEFEC",
          border: "2px solid",
          borderColor: "error.light",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 2,
        }}
      >
        <RoundedIcon Icon={ExclamationIcon} variant="warning" />
      </Box>
      <Typography ref={headlineRef} tabIndex={-1} variant="h4" component="h2" fontFamily="Fortescue, serif" mb={2}>
        {headline}
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={2}>
        {bankExtractionCopy.totalFailureSubtext}
      </Typography>
      <Box component="ul" textAlign="left" sx={{ maxWidth: 480, mx: "auto", mb: 4 }}>
        {bankExtractionCopy.totalFailureBullets.map((bullet) => (
          <Typography component="li" variant="body2" key={bullet} mb={1}>
            {bullet}
          </Typography>
        ))}
      </Box>
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2} justifyContent="center">
        <Button variant="contained" onClick={onTryAnother}>
          {bankExtractionCopy.tryAnotherDocument}
        </Button>
        <Button variant="outlined" onClick={onFillManually}>
          {bankExtractionCopy.fillInManually}
        </Button>
      </Box>
    </Box>
  );
};

export default memo(BankExtractionTotalFailureCard);
